// eslint-disable-next-line max-classes-per-file
import React, { Component, useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import Iframe from 'react-iframe';
import Chart from 'react-google-charts';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import { fetchApiRequest, getApiHost, isMasterOrg, loadOrgs, renderBoilerPlate } from './Common';
import { UserContext } from './UserContext';
import { useTheme } from './ThemeContext';

// Wrapper components to provide theme context to class components
const AnnotatorStatsWithTheme = (props) => {
  const { darkMode } = useTheme();
  return <AnnotatorStats {...props} darkMode={darkMode} />;
};

const AnnotationEventStatsWithTheme = (props) => {
  const { darkMode } = useTheme();
  return <AnnotationEventStats {...props} darkMode={darkMode} />;
};

const AudioClipStatsWithTheme = (props) => {
  const { darkMode } = useTheme();
  return <AudioClipStats {...props} darkMode={darkMode} />;
};

const AudioClipDailyStatsWithTheme = (props) => {
  const { darkMode } = useTheme();
  return <AudioClipDailyStats {...props} darkMode={darkMode} />;
};

const NumEventAnnotationsWithTheme = (props) => {
  const { darkMode } = useTheme();
  return <NumEventAnnotations {...props} darkMode={darkMode} />;
};

const ConfusionMatricesWithTheme = (props) => {
  const { darkMode } = useTheme();
  return <ConfusionMatrices {...props} darkMode={darkMode} />;
};

const DataStudioReportWithTheme = (props) => {
  const { darkMode } = useTheme();
  return <DataStudioReport {...props} darkMode={darkMode} />;
};

class Dashboard extends Component {
  static contextType = UserContext;

  render() {
    const user = this.context;
    // do not show annotation info nor dashboard for famb customers
    const showAnnotationInfo = user.profile && user.profile.org !== 'famb';
    return renderBoilerPlate(
      <div style={{
          display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        { showAnnotationInfo && (<>
        <div>
          <Paper>
            <Typography variant="subtitle1">Annotator Stats</Typography>
            <AnnotatorStatsWithTheme />
          </Paper>
        </div>
        <div>
          <Paper>
            <Typography variant="subtitle1">Annotation Stats</Typography>
            <AnnotationEventStatsWithTheme />
          </Paper>
        </div>
        <div>
          <Paper>
            <Typography variant="subtitle1">Event Annotations Stats</Typography>
            <NumEventAnnotationsWithTheme />
          </Paper>
        </div>
        </>) }
        <div>
          <Paper>
            <Typography variant="subtitle1">Audio Clip Stats</Typography>
            <AudioClipStatsWithTheme />
          </Paper>
        </div>
        <div>
          <Paper>
            <Typography variant="subtitle1">Audio Clip Daily Stats</Typography>
            <AudioClipDailyStatsWithTheme />
          </Paper>
        </div>
        <div>
          <Paper>
            <Typography variant="subtitle1">Prediction Stats</Typography>
            <PredictionEventStats saved_events={false} />
          </Paper>
        </div>
        <div>
          <Paper>
            <Typography variant="subtitle1">Saved Prediction Stats</Typography>
            <PredictionEventStats saved_events={true} />
          </Paper>
        </div>
        <div>
          <Paper>
            <Typography variant="subtitle1">Confusion Matrices</Typography>
            <Typography variant="body2">
              Ratio of correctly and wrongly predicted events based on the
              test data.
            </Typography>
            <ConfusionMatricesWithTheme />
          </Paper>
        </div>
        { showAnnotationInfo && (<>
        <div>
          <DataStudioReportWithTheme />
        </div>
        </>)}
      </div>,
    );
  }
}

class AnnotatorStats extends Component {
  static contextType = UserContext;

  constructor(props) {
    super(props);
    this.state = {
      annotators: null,
    };
  }

  componentDidMount() {
    getApiHost((apiHost) => {
      const apiUrl = `${apiHost}/v1/annotation_stats`;
      fetchApiRequest(apiUrl, (fetch) =>
        fetch
          .then((res) => res.json())
          .then((data) => this.setState(data))
          .catch((e) => console.log(`fetch event error: ${e}`)),
      );
    });
  }

  render() {
    const { annotators } = this.state;
    if (!annotators) return <LinearProgress />;

    // Convert data to Google Charts format
    const chartData = [['Date']];
    const annotatorNames = Object.keys(annotators);

    // Add annotator names as column headers
    chartData[0].push(...annotatorNames);

    // Get all unique dates
    const allDates = new Set();
    Object.values(annotators).forEach(annotatorData => {
      annotatorData.forEach(day => allDates.add(day.date));
    });

    // Sort dates
    const sortedDates = Array.from(allDates).sort();

    // Create data rows
    sortedDates.forEach(date => {
      const row = [new Date(date)];
      annotatorNames.forEach(name => {
        const annotatorData = annotators[name];
        const dayData = annotatorData.find(day => day.date === date);
        row.push(dayData ? dayData.count : 0);
      });
      chartData.push(row);
    });

    const options = {
      title: 'Num annotations per IP address',
      legend: { position: 'top' },
      isStacked: true,
      backgroundColor: 'transparent',
      titleTextStyle: {
        color: this.props.darkMode ? '#ffffff' : '#000000',
      },
      legend: {
        position: 'top',
        textStyle: {
          color: this.props.darkMode ? '#ffffff' : '#000000',
        },
        maxLines: 50,
        maxTextLines: 1,
      },
      chartArea: {
        backgroundColor: 'transparent',
        left: 60,
        right: 20,
        bottom: 40,
      },
      hAxis: {
        title: 'Date',
        titleTextStyle: {
          color: this.props.darkMode ? '#ffffff' : '#000000',
        },
        textStyle: {
          color: this.props.darkMode ? '#ffffff' : '#000000',
        },
        gridlines: {
          color: '#404040',
        },
        minorGridlines: {
          color: '#404040',
        },
      },
      vAxis: {
        title: 'Num annotations per IP address',
        // scaleType: 'log',
        titleTextStyle: {
          color: this.props.darkMode ? '#ffffff' : '#000000',
        },
        textStyle: {
          color: this.props.darkMode ? '#ffffff' : '#000000',
        },
        gridlines: {
          color: '#404040',
        },
        minorGridlines: {
          color: '#404040',
        },
      },
    };

    return (
      <Chart
        width="731px"
        height="400px"
        chartType="ColumnChart"
        data={chartData}
        options={options}
      />
    );
  }
}

class AnnotationEventStats extends Component {
  static contextType = UserContext;

  constructor(props) {
    super(props);
    this.state = {
      requests: null,
    };
  }

  componentDidMount() {
    getApiHost((apiHost) => {
      const apiUrl = `${apiHost}/v1/num_annotation_requests`;
      fetchApiRequest(apiUrl, fetch => fetch
        .then((res) => res.json())
        .then((data) => this.setState({ requests: data }))
        .catch((e) => console.log(`fetch request error: ${e}`))
      );
    });
  }

  render() {
    if (this.state.requests === null) return <LinearProgress />;

    // Convert data to Google Charts format
    const chartData = [['Date']];
    const requestTypes = Object.keys(this.state.requests);

    // Add request types as column headers
    chartData[0].push(...requestTypes);

    // Get all unique dates
    const allDates = new Set();
    Object.values(this.state.requests).forEach(requestData => {
      Object.keys(requestData).forEach(date => allDates.add(date));
    });

    // Sort dates
    const sortedDates = Array.from(allDates).sort();

    // Create data rows
    sortedDates.forEach(date => {
      const row = [new Date(date)];
      requestTypes.forEach(type => {
        const requestData = this.state.requests[type];
        row.push(requestData[date] || 0);
      });
      chartData.push(row);
    });

    const options = {
      title: 'Num annotations per request',
      legend: { position: 'top' },
      isStacked: true,
      backgroundColor: 'transparent',
      titleTextStyle: {
        color: this.props.darkMode ? '#ffffff' : '#000000',
      },
      legend: {
        position: 'top',
        textStyle: {
          color: this.props.darkMode ? '#ffffff' : '#000000',
        },
        maxLines: 50,
        maxTextLines: 1,
      },
      chartArea: {
        backgroundColor: 'transparent',
        left: 60,
        right: 20,
        bottom: 40,
      },
      hAxis: {
        title: 'Date',
        titleTextStyle: {
          color: this.props.darkMode ? '#ffffff' : '#000000',
        },
        textStyle: {
          color: this.props.darkMode ? '#ffffff' : '#000000',
        },
        gridlines: {
          color: '#404040',
        },
        minorGridlines: {
          color: '#404040',
        },
      },
      vAxis: {
        title: 'Num annotations per request',
        // scaleType: 'log',
        titleTextStyle: {
          color: this.props.darkMode ? '#ffffff' : '#000000',
        },
        textStyle: {
          color: this.props.darkMode ? '#ffffff' : '#000000',
        },
        gridlines: {
          color: '#404040',
        },
        minorGridlines: {
          color: '#404040',
        },
      },
    };

    return (
      <Chart
        width="731px"
        height="400px"
        chartType="ColumnChart"
        data={chartData}
        options={options}
      />
    );
  }
}

class AudioClipStats extends Component {
  static contextType = UserContext;

  constructor(props) {
    super(props);
    this.state = {
      clip_stats: null,
    };
    this.componentDidMount = this.componentDidMount.bind(this);
  }

  componentDidMount() {
    getApiHost(apiHost => {
      const apiUrl = `${apiHost}/v1/audio_clip_stats`;
      fetchApiRequest(apiUrl, fetch => fetch
        .then((res) => res.json())
        .then((data) => this.setState({ clip_stats: data }))
        .catch((e) => console.log(`fetch request error: ${e}`))
      );
    });
  }

  render() {
    if (this.state.clip_stats === null)
      return <LinearProgress />;

    const chart_data = [
        ['Device ID', '# audio clips'],
    ].concat(Object.entries(
        this.state.clip_stats
      ).sort(
        (a, b) => a[1] > b[1] ? -1 : 1
      ).map(e => [e[0], e[1]])
    );
    const chart_height = 50 + chart_data.length * 22;
    return (
      <Chart
        width="731px"
        height={chart_height + "px"}
        chartType="BarChart"
        data={chart_data}
        options={{
          legend: { position: 'bottom' },
          isStacked: true,
          vAxis: { title: 'device_id' },
          hAxis: { scaleType: 'log' },
          chartArea: {
            top: 10,
            right: 40,
            bottom: 70
          },
          backgroundColor: 'transparent',
          titleTextStyle: {
            color: this.props.darkMode ? '#ffffff' : '#000000',
          },
          legend: {
            position: 'bottom',
            textStyle: {
              color: this.props.darkMode ? '#ffffff' : '#000000',
            },
            maxLines: 10,
            maxTextLines: 1,
          },
          hAxis: {
            scaleType: 'log',
            textStyle: {
              color: this.props.darkMode ? '#ffffff' : '#000000',
            },
            gridlines: {
              color: '#404040',
            },
            minorGridlines: {
              color: '#404040',
            },
          },
          vAxis: {
            title: 'device_id',
            textStyle: {
              color: this.props.darkMode ? '#ffffff' : '#000000',
            },
          },
        }}
      />
    );
  }
}

class AudioClipDailyStats extends Component {
  static contextType = UserContext;

  constructor(props) {
    super(props);
    this.state = {
      clip_daily_stats: null,
    };
    this.componentDidMount = this.componentDidMount.bind(this);
  }

  componentDidMount() {
    getApiHost((apiHost) => {
      const apiUrl = `${apiHost}/v1/audio_clip_daily_stats`;
      fetchApiRequest(apiUrl, fetch => fetch
        .then((res) => res.json())
        .then((data) => this.setState({ clip_daily_stats: data }))
        .catch((e) => console.log(`fetch request error: ${e}`))
      );
    });
  }

  render() {
    if (this.state.clip_daily_stats === null)
      return <LinearProgress />;

    return (
      <Chart
        width="731px"
        chartType="ColumnChart"
        data={this.state.clip_daily_stats}
        options={{
          height: 400,
          isStacked: true,
          chartArea: {
            top: 80,
            left: 80,
            right: 40,
            bottom: 70
          },
          backgroundColor: 'transparent',
          titleTextStyle: {
            color: this.props.darkMode ? '#ffffff' : '#000000',
          },
          legend: {
            position: 'top',
            textStyle: {
              color: this.props.darkMode ? '#ffffff' : '#000000',
            },
            maxLines: 10,
            maxTextLines: 1,
          },
          hAxis: {
            textStyle: {
              color: this.props.darkMode ? '#ffffff' : '#000000',
            },
          },
          vAxis: {
            title: 'num clips',
            textStyle: {
              color: this.props.darkMode ? '#ffffff' : '#000000',
            },
            gridlines: {
              color: '#404040',
            },
            minorGridlines: {
              color: '#404040',
            },
          },
        }}
      />
    );
  }
}

class NumEventAnnotations extends Component {
  static contextType = UserContext;

  constructor(props) {
    super(props);
    this.state = {
      num_annotations: null,
    };
  }

  componentDidMount() {
    getApiHost((apiHost) => {
      const apiUrl = `${apiHost}/v1/num_event_annotations_request`;
      fetchApiRequest(apiUrl, fetch => fetch
        .then((res) => res.json())
        .then((data) => this.setState({ num_annotations: data }))
        .catch((e) => console.log(`fetch request error: ${e}`))
      );
    });
  }

  render() {
    if (this.state.num_annotations === null) return <LinearProgress />;
    const static_data = {
      sesa: {'Casual': 140, 'Siren': 90, 'Explosion': 170, 'Gunshot': 80},
      voice: {'Gunshot': 7046, 'Baby cry': 6412, 'Glass break': 7249},
      thunder: {'Raining': 734},
    }
    const data = [['Event', 'SESA', 'VOICe', 'Thunder', 'SAFER']].concat(
      Object.entries(this.state.num_annotations).sort((a, b) => {
        return a[1] > b[1] ? -1 : 1}).map((e => {return [
          e[0],
          static_data.sesa[e[0]] || 0,
          static_data.voice[e[0]] || 0,
          static_data.thunder[e[0]] || 0,
          e[1]
        ]})));
    return (
      <Chart
        width="731px"
        height="700px"
        chartType="BarChart"
        data={data}
        options={{
          title: 'Total number of annotations per event type',
          legend: { position: 'bottom' },
          isStacked: true,
          backgroundColor: 'transparent',
          chartArea: {
            left: 120,
            top: 50,
            right: 40,
            bottom: 70
          },
          titleTextStyle: {
            color: this.props.darkMode ? '#ffffff' : '#000000',
          },
          legend: {
            position: 'bottom',
            textStyle: {
              color: this.props.darkMode ? '#ffffff' : '#000000',
            },
            maxLines: 10,
            maxTextLines: 1,
          },
          hAxis: {
            textStyle: {
              color: this.props.darkMode ? '#ffffff' : '#000000',
            },
            gridlines: {
              color: '#404040',
            },
            minorGridlines: {
              color: '#404040',
            },
          },
          vAxis: {
            textStyle: {
              color: this.props.darkMode ? '#ffffff' : '#000000',
            },
          },
        }}
      />
    );
  }
}

const PredictionEventStats = (props) => {
  const [ chartData, setChartData ] = useState(undefined);
  const { darkMode } = useTheme();

  useEffect(() => {
    if (chartData) return;  // already loaded
    getApiHost(apiHost => {
      const { saved_events } = props;
      const apiUrl = `${apiHost}/v1/prediction_events_chart?` +
        `should_save=${saved_events}`;
      fetchApiRequest(apiUrl, fetch => fetch
        .then(res => res.json())
        .then(json => {
          try {
            // Parse the Altair chart JSON and convert to Google Charts format
            const altairSpec = JSON.parse(json);

            // Extract data from Altair datasets
            let data = null;
            if (altairSpec.datasets) {
              const datasetKeys = Object.keys(altairSpec.datasets);
              if (datasetKeys.length > 0) {
                data = altairSpec.datasets[datasetKeys[0]];
              }
            } else if (altairSpec.data && altairSpec.data.values) {
              data = altairSpec.data.values;
            } else if (altairSpec.data && Array.isArray(altairSpec.data)) {
              data = altairSpec.data;
            } else if (Array.isArray(altairSpec)) {
              data = altairSpec;
            }

            if (!data || !Array.isArray(data)) {
              // Try to create a simple fallback chart
              const fallbackData = [
                ['Date', 'Events'],
                [new Date(), 0]
              ];
              setChartData(fallbackData);
              return;
            }

            // Convert Altair data to Google Charts format
            const googleChartData = [['Date']];
            const events = [...new Set(data.map(d => d.event))];

            // Add event names as column headers
            googleChartData[0].push(...events);

            // Group by date and create rows
            const dateGroups = {};
            data.forEach(d => {
              if (!dateGroups[d.date]) {
                dateGroups[d.date] = {};
              }
              dateGroups[d.date][d.event] = d.value;
            });

            // Create data rows
            Object.keys(dateGroups).sort().forEach(date => {
              const row = [new Date(date)];
              events.forEach(event => {
                row.push(dateGroups[date][event] || 0);
              });
              googleChartData.push(row);
            });

            setChartData(googleChartData);
          } catch (error) {
            // Create a simple fallback chart
            const fallbackData = [
              ['Date', 'Events'],
              [new Date(), 0]
            ];
            setChartData(fallbackData);
          }
        })
        .catch((e) => {
          console.log(`fetch request error: ${e}`);
        })
      );
    });
  }, [chartData]);

  if (!chartData) return <LinearProgress />;

  const options = {
    height: 400,
    legend: { position: 'top' },
    isStacked: true,
    backgroundColor: 'transparent',
    chartArea: {
      top: 100,
      left: 80,
      right: 40,
      bottom: 40
    },
    titleTextStyle: {
      color: darkMode ? '#ffffff' : '#000000',
    },
    legend: {
      position: 'top',
      textStyle: {
        color: darkMode ? '#ffffff' : '#000000',
      },
      maxLines: 10,
      maxTextLines: 1,
    },
    hAxis: {
      title: 'Date',
      textStyle: {
        color: darkMode ? '#ffffff' : '#000000',
      },
      gridlines: {
        color: '#404040',
      },
      minorGridlines: {
        color: '#404040',
      },
},
    vAxis: {
      title: 'Count',
      textStyle: {
        color: darkMode ? '#ffffff' : '#000000',
      },
      gridlines: {
        color: '#404040',
      },
      minorGridlines: {
        color: '#404040',
      },
},
  };

  return (
    <Chart
      width="731px"
      height="400px"
      chartType="ColumnChart"
      data={chartData}
      options={options}
    />
  );
}

class ConfusionMatrices extends Component {
  static contextType = UserContext;

  constructor(props) {
    super(props);
    this.state = {
      matrices: null,
    };
  }

  componentDidMount() {
    getApiHost((apiHost) => {
      const apiUrl = `${apiHost}/v1/confusion_matrices`;
      fetchApiRequest(apiUrl, fetch => fetch
        .then((res) => res.json())
        .then((data) => this.setState({ matrices: data }))
        .catch((e) => console.log(`fetch request error: ${e}`))
      );
    });
  }

  render() {
    if (this.state.matrices === null) return <LinearProgress />;
    return this.state.matrices.map(cm =>
      <div key={cm.name}>
        <Typography variant="subtitle2">{cm.name} model</Typography>
        <div style={{
          backgroundColor: 'transparent',
          display: 'inline-block',
          padding: '10px',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          <img
            src={cm.path}
            alt={cm.name + "Confusion Matrix"}
            style={{
              filter: this.props.darkMode ? 'invert(1) hue-rotate(180deg)' : 'none'
            }}
          />
        </div>
      </div>
    );
  }
}

class DataStudioReport extends Component {
  static contextType = UserContext;

  constructor(props) {
    super(props);
    this.state = {
      report_url: null,
      orgs: [],
      orgFilter: 'master',
    };
  }

  componentDidMount() {
    const user = this.context;
    if (isMasterOrg(user.profile)) {
      loadOrgs(this.context, (orgs) => {
        this.setState({ orgs }, () => this.loadReport(this.state.orgFilter));
      });
    } else {
      this.loadReport(null);
    }
  }

  loadReport(org) {
    getApiHost((apiHost) => {
      const params = org ? `?org=${encodeURIComponent(org)}` : '';
      const apiUrl = `${apiHost}/v1/data_studio_report${params}`;
      fetchApiRequest(apiUrl, fetch => fetch
        .then((res) => res.json())
        .then((data) => this.setState({ report_url: data }))
        .catch((e) => console.log(`fetch request error: ${e}`))
      );
    });
  }

  handleOrgChange = (event) => {
    const orgFilter = event.target.value;
    this.setState({ report_url: null, orgFilter }, () => {
      this.loadReport(orgFilter);
    });
  };

    render() {
    const user = this.context;
    if (this.state.report_url === null) return <LinearProgress />;

    return (
      <div>
        {isMasterOrg(user.profile) && this.state.orgs.length > 0 && (
          <FormControl size="small" sx={{ mb: 2, minWidth: 200 }}>
            <InputLabel id="datastudio-org-label">Organization</InputLabel>
            <Select
              labelId="datastudio-org-label"
              label="Organization"
              value={this.state.orgFilter}
              onChange={this.handleOrgChange}
            >
              {this.state.orgs.map((o) => (
                <MenuItem key={o.name} value={o.name}>{o.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <div style={{
          borderRadius: '4px',
          filter: this.props.darkMode ? 'invert(0.9) hue-rotate(180deg) brightness(0.8)' : 'none'
        }}>
          <Iframe
            width="1000px"
            height="1400px"
            url={this.state.report_url}
            style={{
              border: 'none',
              borderRadius: '4px'
            }}
          />
        </div>
      </div>
    );
  }
}

export default withRouter(Dashboard);
